<template>
  <ClientOnly>
    <Transition name="notification-fade" @enter="onEnter">
      <div
        v-if="show"
        id="notification"
        :class="[
          `fixed z-[3014] flex w-[390px] rounded ${bgColor} shadow-notification p-[14px]`,
          bottomClass,
          description || (showAccept && showCancel)
            ? 'items-start'
            : 'items-center',
        ]"
      >
        <div
          v-if="showIcon"
          class="notification-icon flex text-lg mr-3"
          :class="iconColor"
        >
          <font-awesome-icon :icon="iconForType" />
        </div>

        <div class="notification-body flex-1 pr-10 w-[274px] break-words">
          <div
            v-if="title"
            class="notification-title text-accent-900 font-medium text-base leading-5"
            :class="[description ? 'mb-1' : '']"
          >
            {{ title }}
          </div>
          <div
            v-if="description"
            class="notification-message text-accent-500 font-medium text-base leading-5"
          >
            {{ description }}
          </div>

          <!-- Notification Action Buttons -->
          <div
            class="action-buttons flex gap-3 items-center"
            :class="[showAccept || showCancel ? 'mt-3 ' : '']"
          >
            <button
              v-if="showAccept"
              class="text-accent-900 font-medium text-base underline"
              @click="acceptAction"
            >
              {{ acceptText }}
            </button>
            <div
              v-if="showAccept && showCancel"
              class="separator text-accent-900 text-base"
            >
              .
            </div>
            <button
              v-if="showCancel"
              class="text-accent-900 font-medium text-base"
              @click="cancelAction"
            >
              {{ cancelText }}
            </button>
          </div>
        </div>
        <div v-if="showClose" class="notification-close">
          <button
            class="flex text-base top-2 right-2 text-accent-400 hover:text-accent-700"
            @click="close"
          >
            <font-awesome-icon :icon="['fas', 'xmark-large']" />
          </button>
        </div>
      </div>
    </Transition>
  </ClientOnly>
</template>

<script setup lang="ts">
import { Ref, ref, computed } from "vue";
const props = defineProps({
  show: {
    type: Boolean,
    required: true,
    default: false,
  },
  timeOut: {
    type: Number,
    default: 5,
  },
  showIcon: {
    type: Boolean,
    default: true,
  },
  type: {
    type: String,
    default: "success", // 'info' | 'success' | 'error' | 'warning'
  },
  showClose: {
    type: Boolean,
    default: true,
  },
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    required: false,
    default: "",
  },
  showAccept: {
    type: Boolean,
    default: false,
  },
  showCancel: {
    type: Boolean,
    default: false,
  },
  acceptText: {
    type: String,
    default: "Accept",
  },
  cancelText: {
    type: String,
    default: "Cancel",
  },
  index: {
    type: Number, // The number notification on screen
    default: 0, // We support up to 3 at once
  },
});

const emit = defineEmits(["close", "accept", "cancel"]);

const timer: Ref<number> = ref(props.timeOut * 1000);
const timerId: Ref<ReturnType<typeof setTimeout> | null> = ref(null);
const bottomClass: Ref<string> = computed(
  // If there are multiple notifications on screen, we need to make room
  () => ["bottom-14", "bottom-44", "bottom-[295px]"][props.index],
);

const bgColor = computed(() => {
  switch (props.type) {
    case "error":
      return "bg-danger-100";
    case "warning":
      return "bg-warning-100";
    case "info":
      return "bg-info-100";
    case "success":
    default:
      return "bg-success-100";
  }
});

const iconForType = computed(() => {
  switch (props.type) {
    case "error":
      return ["far", "circle-exclamation"];
    case "warning":
      return ["far", "triangle-exclamation"];
    case "info":
      return ["far", "circle-info"];
    case "success":
    default:
      return ["far", "circle-check"];
  }
});

const iconColor = computed(() => {
  switch (props.type) {
    case "error":
      return "text-danger-600";
    case "warning":
      return "text-warning-600";
    case "info":
      return "text-info-600";
    case "success":
    default:
      return "text-success-700";
  }
});

const close = () => {
  emit("close");

  if (timerId.value) {
    clearTimeout(timerId.value);
  }
};

const acceptAction = () => {
  emit("accept");
};

const cancelAction = () => {
  emit("cancel");
  if (timerId.value) {
    clearTimeout(timerId.value);
  }
};

const onEnter = () => {
  timerId.value = setTimeout(() => {
    emit("close");
  }, timer.value);
};
</script>

<style scoped>
/* Smooth appearance animation */
.notification-fade-enter-active {
  animation: bounce-in 0.4s ease-out;
}
.notification-fade-leave-active {
  transition: opacity 0.3s ease;
  opacity: 0;
}

@keyframes bounce-in {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.15);
  }
  100% {
    transform: scale(1);
  }
}
</style>
